import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { ProjectDetailService } from "shared/services/project";
import {
  GetProjectRevisionService,
  MarkRevisionViewedService,
} from "shared/services/revision";
import {
  GetProjectChatUnreadService,
  MarkProjectChatReadService,
} from "shared/services/notification";
import { handleErrors } from "shared/utils/helper";
import { ProjectDetailTabs } from "shared/utils/enum";
import { Icons } from "assets/icons";
import classNames from "classnames";
import styles from "./styles.module.scss";
import CustomTabs from "shared/components/common/customTabs";
import FilterDropdown from "shared/components/common/filterDropdown";
import ChatBoard from "shared/components/dashboardCommon/tabsContent/chatBoard";
import RevisionsMedia from "shared/components/dashboardCommon/tabsContent/revisionsMedia";
import ProjectOverview from "shared/components/dashboardCommon/tabsContent/projectOverview";
import CustomProjectCard from "shared/components/common/customProjectCard";
import ProjectCardSkeleton from "shared/components/common/projectCardSkeleton";
import Spinner from "shared/components/common/spinner";
import RevisionMediaSkeleton from "shared/components/common/revisionMediaSkeleton";
import ProjectCollaborators from "shared/components/dashboardCommon/tabsContent/projectCollaborators";

const BuilderProjectDetail = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);
  const [revisions, setRevisions] = useState<any[]>([]);
  const [revisionsPage, setRevisionsPage] = useState(1);
  const [hasMoreRevisionsMedia, setHasMoreRevisionsMedia] = useState(true);
  const [selectedChatBoard, setSelectedChatBoard] =
    useState<string>("Client Chat Board");
  const [chatUnread, setChatUnread] = useState(0);
  const auth = useSelector((state: any) => state.root.auth);

  const isDesigner = auth?.user?.user_type === "designer";

  const tabs = [
    {
      icon: <Icons.TabImage />,
      label: "Revisions & Media",
      value: ProjectDetailTabs.REVISIONS_MEDIA,
    },
    {
      icon: <Icons.Chat />,
      label: "Chat Board",
      value: ProjectDetailTabs.CHAT_BOARD,
      showDot: chatUnread > 0,
    },
    {
      icon: <Icons.TabOverview />,
      label: "Project Overview",
      value: ProjectDetailTabs.PROJECT_OVERVIEW,
    },
    {
      icon: <Icons.TabOverview />,
      label: "Contact Info",
      value: ProjectDetailTabs.COLLABORATORS,
    },
  ];

  const tabOrder = tabs.map((t) => t.value);

  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabOrder.includes(tabParam as ProjectDetailTabs)) {
      return tabParam as ProjectDetailTabs;
    }
    return tabOrder[0];
  };

  const [activeTab, setActiveTab] =
    useState<ProjectDetailTabs>(getInitialTab());

  const handleTabChange = (tabValue: string) => {
    const newTab = tabValue as ProjectDetailTabs;
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  useEffect(() => {
    fetchProjectDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chat-board unread bubble: fetch the per-project unread chat count on load.
  useEffect(() => {
    if (!id) return;
    GetProjectChatUnreadService(parseInt(id))
      .then((r) => {
        const data = r.data?.data ?? r.data;
        setChatUnread(Number(data?.unread_count ?? 0));
      })
      .catch(() => {});
  }, [id]);

  // Clear the chat unread bubble when the user opens the Chat Board tab.
  useEffect(() => {
    if (id && activeTab === ProjectDetailTabs.CHAT_BOARD && chatUnread > 0) {
      MarkProjectChatReadService(parseInt(id)).catch(() => {});
      setChatUnread(0);
    }
  }, [activeTab, id, chatUnread]);

  // Live chat unread bubble: bump the count when a comment/reply notification
  // for this project arrives over the socket (no page refresh needed).
  useEffect(() => {
    if (!id) return;
    const handleNewNotification = (event: Event) => {
      const data = (event as CustomEvent<any>)?.detail;
      const type = String(data?.type || "").toLowerCase();
      const isChat = type === "new_comment" || type === "new_reply";
      if (!isChat || Number(data?.project_id) !== parseInt(id)) return;
      if (activeTab === ProjectDetailTabs.CHAT_BOARD) {
        MarkProjectChatReadService(parseInt(id)).catch(() => {});
        return;
      }
      setChatUnread((c) => c + 1);
    };
    window.addEventListener("notification:new", handleNewNotification);
    return () => {
      window.removeEventListener("notification:new", handleNewNotification);
    };
  }, [id, activeTab]);

  useEffect(() => {
    const fromChatBoard = location.state?.fromChatBoard;
    if (fromChatBoard) {
      const chatBoardTab = ProjectDetailTabs.CHAT_BOARD;
      setActiveTab(chatBoardTab);
      setSearchParams({ tab: chatBoardTab });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabOrder.includes(tabParam as ProjectDetailTabs)) {
      setActiveTab(tabParam as ProjectDetailTabs);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id && projectData && activeTab === ProjectDetailTabs.REVISIONS_MEDIA) {
      setRevisionsPage(1);
      setRevisions([]);
      fetchRevisions(1, false);
    }
  }, [id, projectData, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjectDetail = async () => {
    if (!id) {
      setProjectData(location.state?.projectData || {});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await ProjectDetailService(parseInt(id));

      if (response.status === 200) {
        const data = response.data?.data || response.data || {};
        setProjectData(data);
      }
    } catch (error: any) {
      handleErrors(error);
      setProjectData(location.state?.projectData || {});
    } finally {
      setLoading(false);
    }
  };

  const fetchRevisions = async (page: number = 1, append: boolean = false) => {
    if (!id || revisionsLoading) return;

    try {
      setRevisionsLoading(true);
      const limit = 5;
      const response = await GetProjectRevisionService(
        parseInt(id),
        page,
        limit,
      );

      if (response.status === 200) {
        let apiRevisions =
          response.data?.data ||
          response.data?.revisions ||
          response.data ||
          [];

        if (!Array.isArray(apiRevisions)) {
          if (apiRevisions && typeof apiRevisions === "object") {
            apiRevisions =
              Object.values(apiRevisions).filter((item) =>
                Array.isArray(item),
              )[0] || [];
          } else {
            apiRevisions = [];
          }
        }

        const total = response.data?.total;

        if (append) {
          setRevisions((prev) => [...prev, ...apiRevisions]);
        } else {
          setRevisions(apiRevisions);
        }

        setRevisionsPage(page);

        const currentTotal = append
          ? revisions.length + apiRevisions.length
          : apiRevisions.length;
        const hasMore =
          apiRevisions.length === limit &&
          (total === undefined || currentTotal < total);
        setHasMoreRevisionsMedia(hasMore);
      }
    } catch (error: any) {
      handleErrors(error);
      if (!append) {
        setRevisions([]);
        setHasMoreRevisionsMedia(false);
      }
    } finally {
      setRevisionsLoading(false);
    }
  };

  const loadMoreRevisionsMedia = () => {
    if (hasMoreRevisionsMedia && !revisionsLoading) {
      fetchRevisions(revisionsPage + 1, true);
    }
  };

  const finalProjectData = projectData || location.state?.projectData || {};

  const latestRevisionWithStatus =
    revisions.length > 0 ? revisions[0] : finalProjectData?.latest_revision;

  // Record that this user has viewed the current revision (dismisses their
  // "ready for review" banner). Fire-and-forget, once per revision id.
  // Skipped when arriving via the banner's "View Project" button, which must
  // leave the notification card in place.
  const keepReviewBanner = !!location.state?.keepReviewBanner;
  const markedViewedRef = useRef<number | null>(null);
  useEffect(() => {
    if (keepReviewBanner) return;
    const rid = latestRevisionWithStatus?.revision_id;
    if (rid && markedViewedRef.current !== Number(rid)) {
      markedViewedRef.current = Number(rid);
      MarkRevisionViewedService(Number(rid)).catch(() => {
        // fire-and-forget: a failed view-record must not block the page
      });
    }
  }, [latestRevisionWithStatus?.revision_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chatBoards = finalProjectData?.chat_boards || [];

  const generalBoard = chatBoards.find(
    (board: any) => board.type === "general",
  );
  const internalBoard = chatBoards.find(
    (board: any) => board.type === "internal",
  );

  const chatBoardOptions = [];
  if (generalBoard) chatBoardOptions.push("Client Chat Board");
  if (internalBoard) chatBoardOptions.push("Team Chat Board");

  const getSelectedChatBoardId = (): string | number | undefined => {
    if (!isDesigner) {
      return generalBoard?.chat_board_id;
    }

    if (selectedChatBoard === "Client Chat Board" && generalBoard) {
      return generalBoard.chat_board_id;
    }
    if (selectedChatBoard === "Team Chat Board" && internalBoard) {
      return internalBoard.chat_board_id;
    }
    return generalBoard?.chat_board_id || internalBoard?.chat_board_id;
  };

  useEffect(() => {
    if (chatBoards.length > 0) {
      if (!isDesigner) {
        if (generalBoard) {
          setSelectedChatBoard("Client Chat Board");
        }
      } else {
        if (!generalBoard && internalBoard) {
          setSelectedChatBoard("Team Chat Board");
        } else if (generalBoard) {
          setSelectedChatBoard("Client Chat Board");
        }
      }
    }
  }, [finalProjectData?.chat_boards, isDesigner]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentIndex = tabOrder.indexOf(activeTab);

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prevTab = tabOrder[currentIndex - 1] as ProjectDetailTabs;
      handleTabChange(prevTab);
    }
  };

  const handleNext = () => {
    if (currentIndex < tabOrder.length - 1) {
      const nextTab = tabOrder[currentIndex + 1] as ProjectDetailTabs;
      handleTabChange(nextTab);
    }
  };

  if (loading) {
    return (
      <div className={classNames(styles.pageWrapper, "flex flex-col gap-5")}>
        <ProjectCardSkeleton count={1} />
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col gap-5 w-full h-full",
      )}
    >
      <CustomProjectCard
        id={finalProjectData?.project_id || (id ? parseInt(id) : undefined)}
        image={finalProjectData?.project_asset?.path || finalProjectData?.image}
        title={finalProjectData?.name || finalProjectData?.title}
        currentRevision={latestRevisionWithStatus}
        status={finalProjectData?.status}
        isArchived={!!finalProjectData?.is_archived}
        onChatIconClick={() => handleTabChange(ProjectDetailTabs.CHAT_BOARD)}
      />
      <div className={classNames(styles.box)}>
        <div className={styles.tabsNavigationRow}>
          <button
            className={styles.arrowButton}
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <Icons.ChevLeft />
          </button>

          <CustomTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            rightSideContent={
              <>
                {activeTab === ProjectDetailTabs.CHAT_BOARD &&
                  isDesigner &&
                  chatBoardOptions.length > 0 && (
                    <FilterDropdown
                      label=""
                      options={chatBoardOptions}
                      selected={selectedChatBoard}
                      onSelect={(value: string) => setSelectedChatBoard(value)}
                    />
                  )}
              </>
            }
          />

          <button
            className={styles.arrowButton}
            onClick={handleNext}
            disabled={currentIndex === tabOrder.length - 1}
          >
            <Icons.ChevRight />
          </button>
        </div>
        <div className={classNames(styles.tabsContentContainer, "")}>
          {activeTab === ProjectDetailTabs.REVISIONS_MEDIA && (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {revisionsLoading && revisions.length === 0 ? (
                <RevisionMediaSkeleton count={2} />
              ) : (
                <>
                  <RevisionsMedia
                    revisions={revisions}
                    showBadge={false}
                    projectId={id ? parseInt(id) : undefined}
                    projectStatus={finalProjectData?.status}
                    hasMore={hasMoreRevisionsMedia}
                    isLoadingMore={revisionsLoading}
                    onLoadMore={loadMoreRevisionsMedia}
                    onProceedSuccess={async () => {
                      // Refresh project data (status + latest_revision). The
                      // resulting projectData change triggers the revisions
                      // refetch effect above; calling fetchRevisions() here too
                      // races that effect through the revisionsLoading guard and
                      // can leave the list cleared/empty ("No media yet").
                      await fetchProjectDetail();
                    }}
                  />
                  {revisionsLoading && revisions.length > 0 && (
                    <div className="flex items-center justify-center py-8">
                      <Spinner />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {activeTab === ProjectDetailTabs.CHAT_BOARD && (
            <ChatBoard
              projectId={id}
              chatBoardId={getSelectedChatBoardId()}
              isReadOnly={
                finalProjectData?.status?.toLowerCase() === "completed" ||
                !!finalProjectData?.is_archived
              }
            />
          )}
          {activeTab === ProjectDetailTabs.PROJECT_OVERVIEW && (
            <ProjectOverview
              projectId={id ? parseInt(id) : undefined}
              showContract={false}
              questionnaireDocuments={finalProjectData?.questionnaire_documents}
              inspirationFiles={finalProjectData?.inspiration_files}
            />
          )}
          {activeTab === ProjectDetailTabs.COLLABORATORS && (
            <ProjectCollaborators
              projectId={id ? parseInt(id) : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BuilderProjectDetail;
